import { useEffect, useMemo, useRef, useState } from 'react'
import { prefersReducedMotion } from '../../shared/motion.ts'
import plinkoOrb from '../../shared/assets/plinko-ball.webp'

type Props = {
  rows: number
  path: string | null
  slot: number | null
  multipliers: number[]
  animKey: number
  onComplete?: () => void
}

const DEFAULT_ROW_H = 24
const DEFAULT_COL_W = 22
const HOP_PX = 7

function rowHeightForViewport(): number {
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight
  if (window.innerWidth <= 360 && viewportHeight <= 600) return 16
  if (window.innerWidth <= 520 && viewportHeight <= 700) return 18
  return DEFAULT_ROW_H
}

function columnWidthForViewport(): number {
  return window.innerWidth <= 360 ? 20 : DEFAULT_COL_W
}

/** Heat bucket for a slot multiplier within the current table. */
function heatClass(m: number, min: number, max: number): string {
  if (!(m > 0) || max <= min || min <= 0) {
    if (m >= 10) return 'heat-max'
    if (m >= 2) return 'heat-high'
    if (m >= 1) return 'heat-mid'
    return 'heat-low'
  }
  const k = Math.log(m / min) / Math.log(max / min)
  if (k >= 0.88) return 'heat-max'
  if (k >= 0.7) return 'heat-high'
  if (k >= 0.42) return 'heat-mid'
  if (k >= 0.22) return 'heat-low'
  return 'heat-min'
}

/** Server-authoritative path animation: path string "LRLL…" only paints, never decides outcome. */
export function Board({ rows, path, slot, multipliers, animKey, onComplete }: Props) {
  const ballRef = useRef<HTMLImageElement>(null)
  const ghostRefs = useRef<(HTMLImageElement | null)[]>([])
  const onCompleteRef = useRef(onComplete)
  const [landed, setLanded] = useState(false)
  const [flashPeg, setFlashPeg] = useState<{ row: number; col: number } | null>(null)
  const [rowHeight, setRowHeight] = useState(rowHeightForViewport)
  const [columnWidth, setColumnWidth] = useState(columnWidthForViewport)

  useEffect(() => {
    const syncBoardMetrics = () => {
      setRowHeight(rowHeightForViewport())
      setColumnWidth(columnWidthForViewport())
    }
    window.addEventListener('resize', syncBoardMetrics)
    window.visualViewport?.addEventListener('resize', syncBoardMetrics)
    return () => {
      window.removeEventListener('resize', syncBoardMetrics)
      window.visualViewport?.removeEventListener('resize', syncBoardMetrics)
    }
  }, [])

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  // Waypoints from the server path: depth k, col = rights so far.
  const waypoints = useMemo(() => {
    if (!path || path.length === 0) return null
    const pts: { x: number; y: number }[] = [{ x: 0, y: 6 }]
    let rights = 0
    for (let i = 0; i < path.length; i++) {
      if (path[i] === 'R') rights += 1
      const depth = i + 1
      pts.push({ x: (rights - depth / 2) * columnWidth, y: 6 + depth * rowHeight })
    }
    return pts
  }, [columnWidth, path, rowHeight])

  useEffect(() => {
    setLanded(false)
    setFlashPeg(null)
    if (!waypoints) return
    const ball = ballRef.current
    if (!ball) return

    // Reduced motion: hold the stepped behaviour, no hop physics.
    if (prefersReducedMotion()) {
      let i = 0
      ball.style.transform = `translate3d(${waypoints[0].x}px, ${waypoints[0].y}px, 0)`
      const id = window.setInterval(() => {
        i += 1
        if (i >= waypoints.length) {
          window.clearInterval(id)
          setLanded(true)
          setFlashPeg(null)
          onCompleteRef.current?.()
          return
        }
        ball.style.transform = `translate3d(${waypoints[i].x}px, ${waypoints[i].y}px, 0)`
      }, 72)
      return () => window.clearInterval(id)
    }

    let raf = 0
    let seg = 0
    let segStart = performance.now()
    let lastFrame = segStart
    let rotation = 0
    const trail: { x: number; y: number }[] = []

    const tick = (now: number) => {
      const depthRatio = seg / Math.max(1, waypoints.length - 2)
      const segmentMs = 122 - depthRatio * 42
      const p = Math.min(1, (now - segStart) / segmentMs)
      const a = waypoints[seg]
      const b = waypoints[Math.min(seg + 1, waypoints.length - 1)]
      const lateral = p * p * (3 - 2 * p)
      const x = a.x + (b.x - a.x) * lateral
      const hop = HOP_PX * (1 - depthRatio * 0.22)
      const y = a.y + (b.y - a.y) * p - hop * Math.sin(Math.PI * p)
      const direction = b.x >= a.x ? 1 : -1
      rotation += Math.min(24, now - lastFrame) * direction * 0.18
      lastFrame = now
      const impact = p < 1 ? Math.max(0, (p - 0.82) / 0.18) : 0
      ball.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rotation}deg) scale(${1 + impact * 0.1}, ${1 - impact * 0.08})`

      trail.unshift({ x, y })
      if (trail.length > 3) trail.pop()
      ghostRefs.current.forEach((g, gi) => {
        if (!g) return
        const pt = trail[gi + 1]
        g.style.opacity = pt ? String(0.22 - gi * 0.08) : '0'
        if (pt) g.style.transform = `translate3d(${pt.x}px, ${pt.y}px, 0) scale(${0.86 - gi * 0.12})`
      })

      if (p >= 1) {
        seg += 1
        segStart = now
        if (seg >= waypoints.length - 1) {
          setLanded(true)
          setFlashPeg(null)
          onCompleteRef.current?.()
          return // final transform already applied
        }
        // During segment seg → seg+1 the ball bounces off peg (row=seg, col=rights@seg).
        const col = Math.round(waypoints[seg].x / columnWidth + seg / 2)
        setFlashPeg({ row: seg, col })
        raf = requestAnimationFrame(tick)
        return
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [animKey, columnWidth, waypoints])

  const min = multipliers.length ? Math.min(...multipliers) : 0
  const max = multipliers.length ? Math.max(...multipliers) : 0
  const pegRows = rows

  return (
    <div className="plinko-board">
      <div
        className="plinko-pegs"
        style={{ minHeight: pegRows * rowHeight + (rowHeight < DEFAULT_ROW_H ? 24 : 40) }}
      >
        {Array.from({ length: pegRows }, (_, r) => (
          <div
            key={r}
            className="plinko-row"
            style={{ ['--cols' as string]: r + 1, height: rowHeight, gap: columnWidth - 8 }}
          >
            {Array.from({ length: r + 1 }, (_, c) => (
              <span
                key={c}
                className={`plinko-peg${
                  flashPeg && flashPeg.row === r && flashPeg.col === c ? ' flash' : ''
                }`}
              />
            ))}
          </div>
        ))}
        {waypoints && (
          <>
            {[0, 1].map((gi) => (
              <img
                key={gi}
                ref={(el) => {
                  ghostRefs.current[gi] = el
                }}
                className="plinko-ball ghost"
                src={plinkoOrb}
                alt=""
                aria-hidden
                style={{ opacity: 0 }}
              />
            ))}
            <img
              ref={ballRef}
              className={`plinko-ball${landed ? ' landed' : ' is-travelling'}`}
              src={plinkoOrb}
              alt=""
              aria-hidden
            />
          </>
        )}
        {!waypoints && (
          <img className="plinko-idle-orb" src={plinkoOrb} alt="" aria-hidden />
        )}
      </div>
      <div
        className="plinko-slots"
        style={{
          gridTemplateColumns: `repeat(${multipliers.length || rows + 1}, minmax(0, 1fr))`,
          width: `${(multipliers.length || rows + 1) * columnWidth}px`,
        }}
      >
        {multipliers.map((m, i) => {
          const hit = landed && slot === i
          return (
            <div
              key={i}
              className={`plinko-slot ${heatClass(m, min, max)}${hit ? ' hit' : ''}`}
            >
              {m.toFixed(2)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
